import { dataFix } from 'global/common/dataFix';
import { gEnums } from 'global/enums/globalEnums';
import { setStyleAndClass } from 'global/styles/formatting';
import { useCallback, useContext, useRef } from 'react';
import { AppSettingsContext } from '../AppSettingsContext';
import { DemoContext } from '../DemoContext';
import { PreviewContext } from '../PreviewContext';
import { _appSettingsOriginTypes } from '../ServiceWorkerContext';
import { CurrentSettingsContext } from '../SettingsDataContext';
import { PapsContext } from '../PapsContext';
import { currentHelpers } from 'global/redirection/current';
import { _showAppLogs } from '../LogContext';

const _appUserStuff = [
  'appUserDataModifications',
  'appUserDataSettings',
  'appUserImageOptions',
  'appUserAuth',
  'appUserPagePermissions',
  'appUserPermissions',
  'appUserProfileInfo',
  'appUserSettings',
  'appUserUiOptions',
]

export const usePageAreaSettings = () => {

  const appSettingsContext = useContext(AppSettingsContext);
  const { appSettings_state } = appSettingsContext ?? {}

  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}

  const previewContext = useContext(PreviewContext)
  const { preview_state } = previewContext ?? {}

  const demoContext = useContext(DemoContext)
  const { demo_state } = demoContext ?? {}

  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext ?? {}

  const infoRef = useRef({
    appSettings_state,
    currentSettings_state,
    demo_state,
    paps_state,
    preview_state,
  })

  infoRef.current = {
    appSettings_state,
    currentSettings_state,
    demo_state,
    paps_state,
    preview_state,
  }

  const getPageSettings = useCallback(({ aspProps_alt } = null) => {

    const {
      appSettings_state,
      currentSettings_state,
      demo_state,
      paps_state,
      preview_state,
    } = infoRef.current

    const { tempDataSource, settingsPreviewOn } = appSettings_state ?? {}

    /**
     * THIS is the current state of the page
     * determines what the settings are for the page
     */
    const {
      pathViews,
      pathName,
      view,
      viewKey,
      otherView,
      pageKey,
      landingView
    } = paps_state ?? {}

    let trueView = (viewKey || otherView) ? view : view + '_list'
    trueView = currentHelpers.getTrueView(pathViews, view, trueView, landingView, viewKey)

    const aspProps = aspProps_alt || {
      pageKey,
      trueView,
      view,
      viewKey,
    }

    const _aspProps = {
      currentSettings_state,
      demo_state,
      preview_state,
      settingsPreviewOn,
      tempDataSource,
    }

    const asp_props = _.merge({}, _aspProps, aspProps)
    const _pageSettings = getAreaSettingsProps(asp_props)
    _pageSettings._pathName = pathName

    if (_showAppLogs.pageInfo) {
      console.group('🛡️🛡️ getPageSettings', pathName)
      console.log('asp_props', asp_props)
      console.log('_pageSettings', _pageSettings)
      console.log('aps_page', _pageSettings?.aps_page)
      console.log('viewItems', _pageSettings?.aps_page?.viewItems)
      console.groupEnd()
    }

    return { _pageSettings, aspProps }
  }, []);

  return { getPageSettings }

}

const getAreaSettingsProps = (aspProps) => {

  let _settingsSourceType;

  const {
    appDataSource,
    currentSettings_state,
    demo_state,
    pageKey,
    preview_state,
    settingsPreviewOn,
    tempDataSource,
    trueView,
    view,
  } = aspProps

  const { settingsAreaProps } = currentSettings_state ?? {}
  const { settings_currentArea } = settingsAreaProps ?? {}
  // const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { settingsPreviewHasChanged, settings_preview, settings_googleSheets } = preview_state ?? {}

  const {
    global,
    isGlobalStatic,
    isViewStatic,
    missingSettings,
    settingsDocName,
    settingsSourceType,
    status,
    views,
  } = settings_currentArea ?? {}

  _settingsSourceType = settingsSourceType

  const view_page = views?.[trueView] || {}
  const { viewItems: viewItems_page } = view_page ?? {}

  const _aps_appUserSettings = {}

  if (global) {
    _.forEach(global, (value, key) => {
      if (_appUserStuff.includes(key) && value) {
        _.assign(_aps_appUserSettings, value);
      }
    });
  }

  ammendPageViewItems(viewItems_page)

  let aps_page = dataFix.removeNumericKeys(view_page)

  aps_page.trueView = trueView

  if (aps_page?.viewItems) {
    _.forEach(aps_page.viewItems, (value) => {
      if (_.isObject(value)) {
        _.set(value, '_viewPermissionType', 'allow');
      }
    });
  }

  // set the caption if none exists
  if (aps_page && _.isObject(aps_page) && !aps_page.menu) {
    const isList = trueView && trueView.indexOf('_list') >= 0
    const _props = !isList && aps_page && aps_page.viewItems && aps_page.viewItems[view] && aps_page.viewItems[view].props
    const { firstName, lastName } = _props ?? {}
    const _captionProps = firstName && lastName ? ['firstName', 'lastName'] : ['name']
    aps_page.menu = {
      captionType: isList ? gEnums.captionTypes.fixed : gEnums.captionTypes.fromData,
      caption: _.startCase(view),
      captionProps: !isList ? _captionProps : null
    }
  }

  const { useAppUsersAsAppUser } = _aps_appUserSettings ?? {}

  if (useAppUsersAsAppUser) {
    _aps_appUserSettings.appUserCollection = 'appUsers'
    _aps_appUserSettings.appUserCollections = ['appUsers']
  }

  const appSettings = { aps_page, _appSettingsOriginType: _appSettingsOriginTypes.settings, }

  if (settingsPreviewOn && settingsPreviewHasChanged && settings_preview) {
    const { page } = settings_preview ?? {}
    appSettings.aps_page = page
    appSettings._appSettingsOriginType = _appSettingsOriginTypes.preview
  }

  const { appDataSourceType } = appDataSource ?? {}

  const _adst = tempDataSource ? tempDataSource : appDataSourceType

  switch (_adst) {
    case gEnums.dataSourceTypes.googleSheet:
      if (settings_googleSheets) {
        const { views } = settings_googleSheets ?? {}
        const _page = views && trueView && views[trueView] ? views[trueView] : {}
        appSettings.aps_page = _page
        appSettings._appSettingsOriginType = _appSettingsOriginTypes.googleSheets
      }
  }

  if (demo_state && demo_state?.demoOn) {
    const { demoPreview } = demo_state ?? {}
    const { projectSettings } = demoPreview ?? {}
    const { views } = projectSettings ?? {}
    const _page = views && trueView && views[trueView] ? views[trueView] : {}
    appSettings.aps_page = _page
    appSettings._appSettingsOriginType = _appSettingsOriginTypes.demo
  }

  const _appSettings = appSettings

  const ps = {
    settingsDocName,
    ..._appSettings,
    isGlobalStatic,
    isViewStatic,
    pageSettingsKey: pageKey,
    missingSettings,
    status,
    settingsSourceType
  }

  return ps

}

const ammendPageViewItems = (viewItems) => {
  if (viewItems) {
    Object.keys(viewItems).forEach(viKey => {
      if (!viewItems[viKey].key) {
        if (_.isObject(viewItems[viKey])) {
          viewItems[viKey].key = viKey
        }
      }
    })
  }
  return viewItems
}

